import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationExtras, ParamMap, Router } from '@angular/router';
import { CoreService, DialogResult, DialogService, DspxCardSetBaseFormComponent, SpinnerService } from 'dspx-core';
import { finalize, take } from 'rxjs';
import { API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { POP_UP_CODES, POP_UP_STYLE_CLASS } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES } from '../../constants/templates.constants';
import { CareAllowancePlacementAdjustmentGridFilterRequest } from '../../interfaces/care-allowance-placement-adjustment-grid-filter-request.interface';
import { CareAllowancePlacementAdjustmentInvoiceListResponse } from '../../interfaces/care-allowance-placement-adjustment-invoice-list-response.interface';
import { CareAllowancePlacementAdjustmentLineListResponse } from '../../interfaces/care-allowance-placement-adjustment-line-list-response.interface';
import { CareAllowancePlacementAdjustmentOverpaymentRecoveryScheduleResponse } from '../../interfaces/care-allowance-placement-adjustment-overpayment-recovery-schedule-response.interface';
import { AppDataService } from '../../services/app-data.service';
import { ErrorService } from '../../services/error.service';
import { ResponseParserService } from '../../services/response-parser.service';
import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';

@Component({
  selector: 'app-care-allowance-placement-adjustment-list',
  templateUrl: './care-allowance-placement-adjustment-list.component.html',
  styleUrl: './care-allowance-placement-adjustment-list.component.scss'
})
export class CareAllowancePlacementAdjustmentListComponent extends BaseCardSetListComponent implements OnInit, OnDestroy {
  @ViewChild(DspxCardSetBaseFormComponent)
  private set _baseForm(value: DspxCardSetBaseFormComponent) {
    this.baseForm = value
  }

  activeDialog: string = '';
  private routePrefix = '';
  private placementId = '';

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected spinnerService: SpinnerService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected appDataService: AppDataService,
    private coreService: CoreService,
    private errorService: ErrorService,
  ) {
    super(
      router,
      spinnerService,
      dialogService,
      responseParserService,
      appDataService,
      TEMPLATES.careAllowancePlacementAdjustmentList,
      () => {
        return API_ENDPOINTS.dashboard.getCareAllowancePlacementAdjustments
      },
      false);
  }

  ngOnInit(): void {
    // We get the first part of the URL so that we can navigate back to that route after the Cancel or Save button is clicked.
    let index = this.router.url.indexOf(`/${ROUTE_CONSTANTS.careAllowancePlacementAdjustment.listPrefix}`);
    this.routePrefix = this.router.url.substring(0, index);

    this.registerSubscription(
      this.route.paramMap.subscribe((paramMap: ParamMap) => {
        const placementId = paramMap.get('placementId');
        if (placementId) {
          this.placementId = placementId;
        }
      })
    );

    this.coreService.applicationFunctions = {
      exit: () => this.navigateBack(),
    };
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  formLoad(event: any): void {
    this.loadGridDataOnFormLoad(this.getAdjustmentFilterRequest());
  }

  filterData(event: any): void {
  }

  actionButton_adjustmentGrid_datagrid_custom_createAdjustment_Clicked(event: any) {
    const navExtras = this.getNavigationExtras();
    this.router.navigate([this.router.url, ROUTE_CONSTANTS.careAllowancePlacementAdjustment.prefix], navExtras);
  }

  actionButton_adjustmentGrid_datagrid_viewInvoices_Clicked(event: any) {
    this.showAdjustmentInvoicesAsync(event.event.data.id);
  }

  actionButton_adjustmentGrid_datagrid_viewOverpaymentRecoverySchedule_Clicked(event: any) {
    this.showAdjustmentOverpaymentRecoveryScheduleAsync(event.event.data.id);
  }

  actionButton_adjustmentGrid_datagrid_viewAdjustmentLines_Clicked(event: any) {
    this.showAdjustmentLinesAsync(event.event.data.id);
  }

  actionButton_adjustmentGrid_datagrid_view_Clicked(event: any) {
    const navExtras = this.getNavigationExtras();
    this.router.navigate([this.router.url, ROUTE_CONSTANTS.careAllowancePlacementAdjustment.prefix, event.event.data.id], navExtras);
  }

  actionButton_adjustmentGrid_datagrid_cancelAdjustment_Clicked(event: any) {
    this.activeDialog = POP_UP_CODES.careAllowancePlacementAdjustmentList.cancelAdjustment;
    this.dialogService.showDialog(this.activeDialog, event.event.data);
  }

  dialogButton_cancelAdjustment_Clicked(event: any): void {
    this.activeDialog = POP_UP_CODES.careAllowancePlacementAdjustmentList.confirmCancelAdjustment;
    this.registerSubscription(
      this.dialogService
        .showConfirm(this.activeDialog, { transactionTypeCode: event.transactionTypeCode, transactionTypeValue: event.transactionTypeValue })
        .subscribe((response) => {
          if (response === DialogResult.ACCEPTED) {
            this.spinnerService.showSpinner();
            this.appDataService
              .post(API_ENDPOINTS.careAllowancePlacementAdjustment.cancel, { adjustmentId: event.id, cancelComment: event.cancelledComments})
              .pipe(
                take(1),
                this.errorService.errorHandler(),
                finalize(() => this.spinnerService.hideSpinner())
              )
              .subscribe((data) => {
                this.loadGridDataOnFormLoad(this.getAdjustmentFilterRequest());
              });
          }
        })
    );
  }

  private getAdjustmentFilterRequest(): CareAllowancePlacementAdjustmentGridFilterRequest {
    return {
      placementId: this.placementId
    };
  }

  private navigateBack() {
    const navExtras = this.getNavigationExtras();
    this.router.navigate([this.routePrefix], navExtras);
  }

  private getNavigationExtras(): NavigationExtras {
    return {
      state: {
        placementFilterRequest: history.state.placementFilterRequest,
        personFilterRequest: history.state.personFilterRequest,
      }
    };
  }

  private async showAdjustmentInvoicesAsync(adjustmentId) {
    const response: CareAllowancePlacementAdjustmentInvoiceListResponse = await this.getAdjustmentInvoicesAsync(adjustmentId);

    if (response != null) {
      const messagePlaceholders = {
        eppPlacementId: response.eppPlacementId,
        eppPlacementAdjustmentId: response.eppPlacementAdjustmentId,
        transactionType: response.transactionType,
        adjustmentComments: response.adjustmentComments
      };

      const popupModel = {
        'invoiceGrid-totalRecords': response.gridData.length,
        'invoiceGrid': response.gridData
      };

      this.dialogService.showDialog(POP_UP_CODES.careAllowancePlacementAdjustmentList.invoices, popupModel, messagePlaceholders, true, POP_UP_STYLE_CLASS.dialogXl);
    }
  }

  private async getAdjustmentInvoicesAsync(adjustmentId: string): Promise<any> {
    this.spinnerService.showSpinner();
    let data: any;
    data = await this.appDataService
      .postAndFetchData(`${API_ENDPOINTS.careAllowancePlacement.getAdjustmentInvoices}/${adjustmentId}`)
      .pipe(
        take(1),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .toPromise();

    return data;
  }

  private async showAdjustmentOverpaymentRecoveryScheduleAsync(adjustmentId) {
    const response: CareAllowancePlacementAdjustmentOverpaymentRecoveryScheduleResponse = await this.getOverpaymentRecoveryScheduleAsync(adjustmentId);

    if (response != null) {
      const messagePlaceholders = {
        eppPlacementId: response.eppPlacementId,
        eppPlacementAdjustmentId: response.eppPlacementAdjustmentId,
        transactionType: response.transactionType,
        adjustmentComments: response.adjustmentComments
      };

      const popupModel = {
        'overpaymentRecoveryScheduleGrid-totalRecords': response.gridData.length,
        'overpaymentRecoveryScheduleGrid': response.gridData
      };

      this.dialogService.showDialog(POP_UP_CODES.careAllowancePlacementAdjustmentList.overpaymentRecoverySchedule, popupModel, messagePlaceholders, true, POP_UP_STYLE_CLASS.dialogXl);
    }
  }

  private async getOverpaymentRecoveryScheduleAsync(adjustmentId: string): Promise<any> {
    this.spinnerService.showSpinner();
    let data: any;
    data = await this.appDataService
      .postAndFetchData(`${API_ENDPOINTS.careAllowancePlacement.getAdjustmentOverpaymentRecoverySchedule}/${adjustmentId}`)
      .pipe(
        take(1),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .toPromise();

    return data;
  }

  private async showAdjustmentLinesAsync(adjustmentId) {
    const response: CareAllowancePlacementAdjustmentLineListResponse = await this.getAdjustmentLinesAsync(adjustmentId);

    if (response != null) {
      const messagePlaceholders = {
        eppPlacementId: response.eppPlacementId,
        eppPlacementAdjustmentId: response.eppPlacementAdjustmentId,
        transactionType: response.transactionType,
        adjustmentComments: response.adjustmentComments
      };

      const popupModel = {
        'adjustmentLineGrid-totalRecords': response.gridData.length,
        'adjustmentLineGrid': response.gridData
      };

      this.dialogService.showDialog(POP_UP_CODES.careAllowancePlacementAdjustmentList.adjustmentLines, popupModel, messagePlaceholders, true, POP_UP_STYLE_CLASS.dialogXl);
    }
  }

  private async getAdjustmentLinesAsync(adjustmentId: string): Promise<any> {
    this.spinnerService.showSpinner();
    let data: any;
    data = await this.appDataService
      .postAndFetchData(`${API_ENDPOINTS.careAllowancePlacement.getAdjustmentLines}/${adjustmentId}`)
      .pipe(
        take(1),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .toPromise();

    return data;
  }
}
